import styled from '@emotion/styled';
import { startCase, toLower } from 'lodash';

const titleCase = str => startCase(toLower(str));

const Container = styled.div`
  pre {
    padding: 16px;
    background: #f8f8f8;
    overflow-x: hidden;
    line-height: 1.3em;
  }
`;

function serializeValue(value, format, enumMap = undefined, itemsFormat = undefined) {
  switch (typeof value) {
    case 'string':
      if (format === 'data-url') {
        const regex = /data:([^;]+)(?:;name=([^;]+))?/;
        const match = value.match(regex);
        if (match) {
          const fileName = match[2];
          return fileName;
        }
        return null;
      }
      if (enumMap && enumMap.has(value)) {
        return enumMap.get(value);
      }
      return value;
    case 'number':
      return String(value);
    default:
      if (Array.isArray(value)) {
        return value.map(val => serializeValue(val, itemsFormat, enumMap)).join(', ');
      }
      return JSON.stringify(value);
  }
}

function makeEnumMap({ schema = {}, uiSchema = {} }) {
  const enumNames = uiSchema['ui:enumNames'];
  const enums = schema.type === 'array' ? schema.items.enum : schema.enum;
  if (!enumNames || !enums) {
    return null;
  }
  return new Map(Object.keys(enums).map(key => [enums[key], enumNames[key] || enums[key]]));
}

const TEXT_VALUE_LENGTH_LIMIT = 95;

function serializeFormData({ formData, schema = {}, uiSchema = {} }, tabs = 0, defaultName = 'root') {
  const fragments = [];
  const { type, format } = schema;
  if (!type) {
    return fragments;
  }
  const uiOptions = uiSchema['ui:options'] || {};
  const title = schema.title || titleCase(defaultName);

  if (type === 'object') {
    if (!formData || Object.keys(formData).length === 0) {
      return fragments;
    }

    const { properties } = schema;
    const { hideLabel } = uiOptions;
    Object.keys(formData).forEach(key => {
      fragments.push(
        ...serializeFormData(
          {
            formData: formData[key],
            schema: properties[key],
            uiSchema: uiSchema[key],
          },
          tabs + (hideLabel ? 0 : 1),
          key
        )
      );
    });
    if (fragments.length > 0 && tabs >= 0 && !hideLabel) {
      fragments.unshift(<b>{`${'  '.repeat(tabs)}${title}\n`}</b>);
    }
  } else {
    const itemsFormat = type === 'array' ? schema.items.format : undefined;
    const enumMap = makeEnumMap({ schema, uiSchema });
    const valueText = serializeValue(formData, format, enumMap, itemsFormat);
    if (valueText?.length) {
      fragments.push(
        <b>{`${'  '.repeat(tabs)}${title}: `}</b>,
        <span>
          {valueText.slice(0, TEXT_VALUE_LENGTH_LIMIT)}
          {valueText.length > TEXT_VALUE_LENGTH_LIMIT ? '...' : ''}
          {'\n'}
        </span>
      );
    }
  }
  return fragments;
}

function shouldShowSummary({ uiSchema = {}, formContext = {} }) {
  const uiOptions = uiSchema['ui:options'] || {};
  const { showSummary } = uiOptions;
  if (!showSummary) {
    return false;
  }
  const { stepProps: { isLastStep, isMultiStep } = {} } = formContext;
  return isLastStep && isMultiStep;
}

export default ({ formData, schema, uiSchema, formContext }) => {
  if (!shouldShowSummary({ uiSchema, formContext })) {
    return null;
  }

  const dataFragments = serializeFormData({ formData, schema, uiSchema }, -1);
  return (
    <Container>
      <pre>{dataFragments}</pre>
    </Container>
  );
};
